import React, { useState, useEffect } from 'react';
import { ShieldCheck, Lock, FileText, Truck, RefreshCw, X, CheckCircle2, Globe, ExternalLink, Mail, Phone, MessageCircle, Sliders, Check } from 'lucide-react';
import { DistributorConfig } from '../types';

interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  distributor: DistributorConfig;
  defaultTab?: 'privacy' | 'terms' | 'shipping' | 'pdpa' | 'cookie';
}

export const PolicyModal: React.FC<PolicyModalProps> = ({
  isOpen,
  onClose,
  distributor,
  defaultTab = 'privacy',
}) => {
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms' | 'shipping' | 'pdpa' | 'cookie'>(defaultTab);

  useEffect(() => {
    if (isOpen && defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  if (!isOpen) return null;

  const domain = 'GIFFFORYOU.COM';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-pink-100 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header Strip with Deep Navy Blue */}
        <div className="bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] text-white px-5 sm:px-6 py-4 flex items-center justify-between border-b border-blue-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 text-white shadow-xs">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-extrabold tracking-tight">นโยบายและเงื่อนไขการใช้บริการ</h3>
                <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white/20 border border-white/30 text-white font-bold">
                  {domain}
                </span>
              </div>
              <p className="text-xs text-blue-200 font-medium">
                ศูนย์รวมนโยบายความเป็นส่วนตัวและข้อกำหนดทางกฎหมาย (PDPA Compliant)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors border border-white/20"
            title="ปิดหน้านี้"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Selection Navigation */}
        <div className="bg-slate-50 border-b border-slate-200 px-4 sm:px-6 pt-3 flex items-center gap-2 overflow-x-auto scrollbar-none shrink-0">
          <button
            onClick={() => setActiveTab('privacy')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-2xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'privacy'
                ? 'bg-white text-[#2A59FE] border-[#2A59FE] shadow-2xs'
                : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-slate-100/80'
            }`}
          >
            <Lock className="w-4 h-4" />
            <span>นโยบายความเป็นส่วนตัว (Privacy Policy)</span>
          </button>

          <button
            onClick={() => setActiveTab('pdpa')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-2xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'pdpa'
                ? 'bg-white text-[#2A59FE] border-[#2A59FE] shadow-2xs'
                : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-slate-100/80'
            }`}
          >
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>การคุ้มครองข้อมูล PDPA</span>
          </button>

          <button
            onClick={() => setActiveTab('cookie')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-2xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'cookie'
                ? 'bg-white text-[#2A59FE] border-[#2A59FE] shadow-2xs'
                : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-slate-100/80'
            }`}
          >
            <Sliders className="w-4 h-4 text-indigo-600" />
            <span>นโยบายการใช้คุกกี้ (Cookie Policy)</span>
          </button>

          <button
            onClick={() => setActiveTab('terms')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-2xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'terms'
                ? 'bg-white text-[#2A59FE] border-[#2A59FE] shadow-2xs'
                : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-slate-100/80'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>เงื่อนไขข้อตกลงการใช้บริการ</span>
          </button>

          <button
            onClick={() => setActiveTab('shipping')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-2xl text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'shipping'
                ? 'bg-white text-[#2A59FE] border-[#2A59FE] shadow-2xs'
                : 'text-slate-600 hover:text-slate-900 border-transparent hover:bg-slate-100/80'
            }`}
          >
            <Truck className="w-4 h-4" />
            <span>นโยบายจัดส่งและคืนสินค้า</span>
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="p-5 sm:p-8 overflow-y-auto space-y-6 text-slate-700 leading-relaxed text-xs sm:text-sm flex-1">
          {activeTab === 'privacy' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="p-4 rounded-2xl bg-pink-50/60 border border-pink-100 flex items-start gap-3">
                <Globe className="w-5 h-5 text-[#E60067] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                    นโยบายความเป็นส่วนตัวสำหรับโดเมน {domain}
                  </h4>
                  <p className="text-xs text-slate-600 mt-1">
                    ปรับปรุงล่าสุด: มีนาคม 2026 | เว็บไซต์ {domain} ให้ความสำคัญสูงสุดในการคุ้มครองข้อมูลส่วนบุคคลของท่านตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA)
                  </p>
                </div>
              </div>

              <section className="space-y-2">
                <h5 className="font-bold text-slate-900 text-sm flex items-center gap-1.5 text-[#E60067]">
                  <CheckCircle2 className="w-4 h-4" />
                  1. ข้อมูลส่วนบุคคลที่เราจัดเก็บ
                </h5>
                <p className="text-slate-600">
                  เพื่อให้บริการสั่งซื้อผลิตภัณฑ์กิฟฟารีนแก่นักธุรกิจและผู้บริโภคได้อย่างถูกต้อง เว็บไซต์ <span className="font-semibold text-slate-800">{domain}</span> อาจรวบรวมข้อมูลส่วนบุคคลของท่านเมื่อท่านทำรายการสั่งซื้อ ดังนี้:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li>ข้อมูลการติดต่อ: ชื่อ-นามสกุล, เบอร์โทรศัพท์, ที่อยู่จัดส่งพัสดุ, LINE ID</li>
                  <li>ข้อมูลคำสั่งซื้อ: รายการสินค้า, รหัสสินค้า, จำนวนเงิน, สลิปโอนเงิน (ถ้ามี)</li>
                  <li>ข้อมูลทางเทคนิค: บันทึกเข้าชมเบราว์เซอร์, Cookie Preferences เพื่ออำนวยความสะดวกในการใช้งานตะกร้าสินค้า</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h5 className="font-bold text-slate-900 text-sm flex items-center gap-1.5 text-[#E60067]">
                  <CheckCircle2 className="w-4 h-4" />
                  2. วัตถุประสงค์ในการจัดเก็บและใช้งานข้อมูล
                </h5>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li>เพื่อดำเนินการจัดส่งสินค้ากิฟฟารีนถึงที่อยู่ของท่านผ่านบริษัทขนส่งเอกชน (Flash Express, Kerry, ไปรษณีย์ไทย)</li>
                  <li>เพื่อยืนยันการรับชำระเงิน ตรวจสอบสลิป หรือบริการชำระเงินปลายทาง (COD)</li>
                  <li>เพื่อบันทึกสะสมคะแนน PV สมาชิกกิฟฟารีนให้ถูกต้องตามรหัสตัวแทน</li>
                  <li>เพื่อการให้บริการหลังการขาย การรับประกันสินค้า และติดต่อแจ้งสถานะพัสดุ</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h5 className="font-bold text-slate-900 text-sm flex items-center gap-1.5 text-[#E60067]">
                  <CheckCircle2 className="w-4 h-4" />
                  3. การรักษาความปลอดภัยและการเปิดเผยข้อมูล
                </h5>
                <p className="text-slate-600">
                  ทางเราจะไม่ขาย ให้เช่า หรือเปิดเผยข้อมูลส่วนบุคคลของท่านแก่บุคคลภายนอกโดยเด็ดขาด เว้นแต่ได้รับความยินยอมจากท่าน หรือเป็นการเปิดเผยแก่ผู้ให้บริการขนส่งที่จำเป็นสำหรับการส่งมอบพัสดุ
                </p>
              </section>
            </div>
          )}

          {activeTab === 'pdpa' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-100 flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                    การปฏิบัติตามกฎหมาย PDPA (Personal Data Protection Act)
                  </h4>
                  <p className="text-xs text-slate-600 mt-1">
                    สิทธิของเจ้าของข้อมูลส่วนบุคคลสำหรับผู้ใช้งานเว็บไซต์ {domain}
                  </p>
                </div>
              </div>

              <section className="space-y-2">
                <h5 className="font-bold text-slate-900 text-sm text-emerald-800">
                  สิทธิของท่านตามกฎหมาย PDPA:
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs">
                    <span className="font-bold text-slate-800 block text-xs">สิทธิขอเข้าถึงและขอรับคัดลอกข้อมูล</span>
                    <span className="text-[11px] text-slate-500">ท่านมีสิทธิขอเข้าถึงและรับสำเนาข้อมูลส่วนบุคคลที่จัดเก็บในระบบ</span>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs">
                    <span className="font-bold text-slate-800 block text-xs">สิทธิขอให้แก้ไขข้อมูลให้ถูกต้อง</span>
                    <span className="text-[11px] text-slate-500">ท่านมีสิทธิขอแก้ไขข้อมูลการจัดส่ง ชื่อ เบอร์โทรศัพท์ให้เป็นปัจจุบัน</span>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs">
                    <span className="font-bold text-slate-800 block text-xs">สิทธิขอให้ลบหรือทำลายข้อมูล</span>
                    <span className="text-[11px] text-slate-500">ท่านสามารถแจ้งความประสงค์ขอให้ลบประวัติข้อมูลการติดต่อเมื่อสิ้นสุดธุรกรรม</span>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs">
                    <span className="font-bold text-slate-800 block text-xs">สิทธิเพิกถอนความยินยอม</span>
                    <span className="text-[11px] text-slate-500">ท่านสามารถเพิกถอนการยินยอมรับข้อมูลข่าวสารโปรโมชั่นได้ทุกเมื่อ</span>
                  </div>
                </div>
              </section>

              <section className="p-4 rounded-2xl bg-slate-100 border border-slate-200 space-y-2">
                <h5 className="font-bold text-slate-900 text-xs sm:text-sm">
                  เจ้าหน้าที่คุ้มครองข้อมูลส่วนบุคคล (DPO Contact for {domain}):
                </h5>
                <div className="text-xs space-y-1 text-slate-600">
                  <p><span className="font-semibold text-slate-800">ศูนย์ผู้ดูแล:</span> {distributor.distributorName || 'ศูนย์ตัวแทนจำหน่ายกิฟฟารีนออนไลน์'}</p>
                  <p><span className="font-semibold text-slate-800">อีเมลคุ้มครองข้อมูล:</span> <a href="mailto:dlgiffarine@gmail.com" className="text-pink-600 underline">dlgiffarine@gmail.com</a></p>
                  <p><span className="font-semibold text-slate-800">LINE ID:</span> {distributor.lineId?.startsWith('@') ? distributor.lineId : `@${distributor.lineId || '997wvsow'}`}</p>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'cookie' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="p-4 rounded-2xl bg-pink-50/70 border border-pink-100 flex items-start gap-3">
                <Sliders className="w-6 h-6 text-[#DB2777] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                    นโยบายการใช้คุกกี้ (Cookie Policy) สำหรับเว็บไซต์ {domain}
                  </h4>
                  <p className="text-xs text-slate-600 mt-1">
                    เว็บไซต์ {domain} มีการใช้งานคุกกี้เพื่อมอบประสบการณ์การใช้งานเว็บไซต์ที่ดียิ่งขึ้นให้แก่คุณ รวมถึงเสนอสิทธิประโยชน์ที่ตรงใจ ตามหลักมาตรฐาน PDPA
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h5 className="font-bold text-slate-900 text-sm text-[#DB2777]">
                  ประเภทของคุกกี้ที่เราใช้งาน:
                </h5>

                <div className="space-y-3">
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <h6 className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5">
                        <Lock className="w-4 h-4 text-emerald-600" />
                        1. คุกกี้ที่จำเป็นอย่างยิ่ง (Strictly Necessary Cookies)
                      </h6>
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">
                        เปิดตลอดเวลา
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      คุกกี้ประเภทนี้มีความจำเป็นต่อการทำงานของเว็บไซต์ เพื่อให้ท่านสามารถเข้าถึงข้อมูลและใช้งานได้อย่างปลอดภัย เช่น การจดจำสินค้าในตะกร้า การเข้าสู่ระบบสมาชิก และการยืนยันคำสั่งซื้อ
                    </p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <h6 className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-blue-600" />
                        2. คุกกี้เพื่อการวิเคราะห์และวัดผล (Analytics Cookies)
                      </h6>
                      <span className="text-[10px] bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded-full">
                        เลือกเปิด/ปิดได้
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      คุกกี้ประเภทนี้ช่วยให้เราทราบถึงปฏิสัมพันธ์ของผู้ใช้งานในการใช้บริการเว็บไซต์ เพื่อนำมาวิเคราะห์และปรับปรุงประสิทธิภาพความรวดเร็วในการแสดงผลสินค้า
                    </p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <h6 className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-amber-600" />
                        3. คุกกี้เพื่อการทำงานของเว็บไซต์ (Functional Cookies)
                      </h6>
                      <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full">
                        เลือกเปิด/ปิดได้
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      ช่วยจดจำข้อมูลอุปกรณ์และค่าที่ท่านกำหนดไว้ เช่น สินค้าที่เคยค้นหา การบันทึกรายการโปรด เพื่อให้ท่านใช้งานเว็บไซต์ได้อย่างสะดวกสบายยิ่งขึ้น
                    </p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <h6 className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-rose-600" />
                        4. คุกกี้เพื่อการตลาดและสิทธิประโยชน์ (Marketing Cookies)
                      </h6>
                      <span className="text-[10px] bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded-full">
                        เลือกเปิด/ปิดได้
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      ช่วยในการนำเสนอข่าวสาร โปรโมชั่น และสิทธิประโยชน์ของกิฟฟารีนที่ตรงใจและเหมาะสมกับความสนใจของท่านมากที่สุด
                    </p>
                  </div>
                </div>
              </section>

              <section className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100 space-y-1 text-xs text-slate-600">
                <p className="font-bold text-slate-800">การจัดการและการเพิกถอนความยินยอมคุกกี้:</p>
                <p>
                  ท่านสามารถปรับเปลี่ยนหรือเพิกถอนความยินยอมในการใช้งานคุกกี้ได้ตลอดเวลาผ่านทางเมนู <strong className="text-emerald-800">"การตั้งค่าคุกกี้"</strong> บริเวณส่วนล่างของเว็บไซต์ (Footer)
                </p>
              </section>
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <section className="space-y-2">
                <h5 className="font-bold text-slate-900 text-sm text-[#E60067]">
                  ข้อตกลงและเงื่อนไขการใช้งานเว็บไซต์ {domain}
                </h5>
                <p className="text-slate-600">
                  การเข้าใช้งานสั่งซื้อสินค้าผ่าน <span className="font-semibold text-slate-800">{domain}</span> ถือว่าท่านตกลงและยอมรับข้อตกลงบริการดังต่อไปนี้:
                </p>
                <ol className="list-decimal pl-5 space-y-2 text-slate-600">
                  <li>
                    <strong className="text-slate-800">การรับประกันสินค้าแท้ 100%:</strong> สินค้าทุกชิ้นบนเว็บไซต์เป็นสินค้าของแท้ 100% เบิกและส่งตรงจากศูนย์กิฟฟารีนอย่างถูกต้องตามกฎหมาย
                  </li>
                  <li>
                    <strong className="text-slate-800">การแสดงราคาและ PV:</strong> ราคาสินค้า คะแนน PV และส่วนลดสมาชิกที่แสดง เป็นไปตามโครงสร้างราคามาตรฐานของบริษัท กิฟฟารีน สกายไลน์ ยูนิตี้ จำกัด
                  </li>
                  <li>
                    <strong className="text-slate-800">การชำระเงิน:</strong> รองรับการโอนเงินผ่านบัญชีธนาคารและการชำระเงินปลายทาง (COD) ผ่านพนักงานขนส่ง
                  </li>
                </ol>
              </section>
            </div>
          )}

          {activeTab === 'shipping' && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <section className="space-y-3">
                <h5 className="font-bold text-slate-900 text-sm text-[#E60067]">
                  นโยบายการจัดส่งสินค้า (Shipping Policy)
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 bg-pink-50/50 rounded-2xl border border-pink-100 text-center">
                    <Truck className="w-6 h-6 text-[#E60067] mx-auto mb-1" />
                    <span className="font-bold text-slate-800 text-xs block">จัดส่งด่วนทั่วไทย</span>
                    <span className="text-[11px] text-slate-500">1 - 3 วันทำการ</span>
                  </div>
                  <div className="p-3.5 bg-amber-50/50 rounded-2xl border border-amber-100 text-center">
                    <ShieldCheck className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                    <span className="font-bold text-slate-800 text-xs block">แพ็คกันกระแทกอย่างดี</span>
                    <span className="text-[11px] text-slate-500">สินค้าปลอดภัย 100%</span>
                  </div>
                  <div className="p-3.5 bg-emerald-50/50 rounded-2xl border border-emerald-100 text-center">
                    <RefreshCw className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <span className="font-bold text-slate-800 text-xs block">ประกันเปลี่ยนสินค้า</span>
                    <span className="text-[11px] text-slate-500">ภายใน 7 วัน</span>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <h6 className="font-bold text-slate-800 text-xs">เงื่อนไขการเปลี่ยน/คืนสินค้า:</h6>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600">
                    <li>กรณีสินค้าชำรุด เสียหาย หรือสูญหายจากการขนส่ง สามารถถ่ายรูป/คลิปวิดีโอแจ้งเคลมสินค้าชิ้นใหม่ได้ทันที</li>
                    <li>สามารถติดต่อแจ้งเคลมผ่าน LINE Official: <span className="font-bold text-emerald-700">{distributor.lineId}</span></li>
                  </ul>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-pink-600" />
            <span>Official Domain: <strong className="text-slate-800 font-mono">GIFFFORYOU.COM</strong></span>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-full bg-[#E60067] hover:bg-[#C20048] text-white font-bold transition-all shadow-xs active:scale-95 ml-auto"
          >
            ตกลง / เข้าใจแล้ว
          </button>
        </div>
      </div>
    </div>
  );
};
